import { Box } from '@mui/material'
import { useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { useLoggedInUser } from '../../../app/hooks/useLoggedInUser'
import { useGetIndustriesQuery, useGetUsersQuery } from '../../../app/redux-fetch/apiQuery'
import { useGetCurrenciesByTenantIdQuery } from '../../../app/redux-fetch/apiOrders'
import FormInput from '../../../components/Forms/FormInput'
import FormSelect from '../../../components/Forms/FormSelect'
import SwitchWithLabels from '../../../components/Switches/SwitchWithLabels'
import { SectionTitleTypography } from '../../../components/Typographies/styles'
import { CustomerFormData } from './Components/validationSchema'
import messages from './messages'

type CustomerDetailProps = {
  isDisabled?: boolean
  isNew?: boolean
}

const CustomerDetail = (props: CustomerDetailProps) => {
  const { isDisabled = false } = props
  const { formatMessage } = useIntl()
  const { data: industries } = useGetIndustriesQuery()
  const { watch, setValue } = useFormContext<CustomerFormData>()
  const { tenantId } = useLoggedInUser()
  const { data: users = [] } = useGetUsersQuery({
    tenantId
  })

  const { data: currencies = [] } = useGetCurrenciesByTenantIdQuery(
    {
      tenantId,
      hideDisabled: true,
    },
    { skip: !tenantId }
  )

  const values = watch()

  const currenciesOptions = useMemo(() => {
    return currencies?.filter((currency: any) => {
      return !currency.customerId
    }).map((currency: any) => ({
      label: `${currency.iso3} - ${currency.name}`,
      value: currency.id
    }))
  }, [currencies, values.id])

  const userOptions = useMemo(() => {
    return users.map((user: any) => ({
      label: `${user.firstName} ${user.lastName}`,
      value: user.id
    }))
  }, [users])

  const industryOptions = useMemo(() => {
    return (industries ?? []).map((industry: any) => ({
      label: industry.name,
      value: industry.id
    }))
  }, [industries])

  return (
    <Box sx={{ pb: '16px' }}>
      <Box sx={{ mb: '16px' }}>
        <SectionTitleTypography>{formatMessage(messages.customerDetail)}</SectionTitleTypography>
      </Box>

      <FormInput
        name="name"
        label={formatMessage(messages.customerName)}
        required
        disabled={isDisabled}
        maxLength={250}
      />

      <FormSelect
        name="accountOwnerId"
        label={formatMessage(messages.accountOwner)}
        options={userOptions}
        required
        disabled={isDisabled}
        placeholder="Please select an account owner"
      />

      <FormInput
        name="alias"
        label={formatMessage(messages.customerAlias)}
        disabled={isDisabled}
        maxLength={250}
      />

      <FormInput
        name="accountNumber"
        label={formatMessage(messages.accountNumber)}
        disabled={isDisabled}
        maxLength={50}
      />

      <FormInput
        name="email"
        label={formatMessage(messages.customerEmail)}
        type="email"
        disabled={isDisabled}
        maxLength={250}
      />

      <FormInput
        name="secondaryEmail"
        label={formatMessage(messages.secondaryEmail)}
        type="email"
        disabled={isDisabled}
        maxLength={250}
      />

      <Box display='flex' columnGap={2} sx={{ mb: '16px' }}>
        <Box flex={1}>
          <FormInput
            name="registrationNumber"
            label={formatMessage(messages.companyRegistrationNumber)}
            disabled={isDisabled}
            maxLength={50}
          />
        </Box>
        <Box flex={1}>
          <FormInput
            name="vatNumber"
            label={formatMessage(messages.VATNumber)}
            disabled={isDisabled}
            maxLength={50}
          />
        </Box>
      </Box>

      <FormSelect
        name="industryId"
        label={formatMessage(messages.industry)}
        options={industryOptions}
        disabled={isDisabled}
      />

      <FormSelect
        name="defaultCurrencyId"
        label={formatMessage(messages.defaultCurrency)}
        options={currenciesOptions}
        required
        disabled={isDisabled}
        placeholder="Please select a default currency"
      />

      <FormInput
        name="webSite"
        label={formatMessage(messages.webSite)}
        disabled={isDisabled}
        maxLength={50}
      />

      <Box sx={{ mb: 2 }}>
        <SwitchWithLabels
          value={values.disabled || false}
          onChange={(value) => setValue('disabled', value)}
          labelBefore="Enabled"
          labelAfter="Disabled"
          disabled={isDisabled}
        />
      </Box>
    </Box>
  )
}

export default CustomerDetail
